import React, { useMemo } from 'react';
import {
  Box,
  Breadcrumbs,
  Card,
  CardContent,
  Divider,
  Link,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useLocation } from 'react-router-dom';
import menuConfig from 'src/configs/menu';

function buildBreadcrumb(levelActual, currentPath) {
  if (!menuConfig || !menuConfig.length) return null;
  let itemActual = menuConfig.find((item) => item.level === levelActual);
  if (!itemActual && currentPath) {
    itemActual = menuConfig.find((item) => item.path === currentPath);
  }
  if (!itemActual) return null;
  const [mod, sec] = itemActual.level.split('.');
  const sectionLevel = `${mod}.${sec}.00.00`;
  const sectionItem = menuConfig.find((item) => item.level === sectionLevel);
  return {
    module: itemActual.module?.trim() ?? null,
    section: sectionItem?.name?.trim() ?? null,
    current: itemActual.name?.trim() ?? null,
  };
}

/**
 * Encabezado de página dentro de una Card con borde superior de color.
 * Incluye breadcrumb automático según el menú activo.
 *
 * Props:
 *  - title        {string}     Título principal (requerido)
 *  - subtitle     {string}     Subtítulo opcional
 *  - icon         {ReactNode}  Ícono MUI opcional
 *  - accentColor  {string}     Color del borde superior y del ícono (default: '#0A395F')
 *  - children     {ReactNode}  Controles centrados en desktop / apilados en mobile
 *  - rightContent {ReactNode}  Contenido alineado a la derecha (solo en desktop)
 *  - sx           {object}     Estilos extra para el Card
 */
export const PageHeaderCard = ({
  title,
  subtitle,
  icon,
  accentColor = '#0A395F',
  children,
  rightContent,
  sx = {},
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const location = useLocation();

  const breadcrumb = useMemo(() => {
    const levelActual = localStorage.getItem('levelMenuActual');
    return buildBreadcrumb(levelActual, location.pathname);
  }, [location.pathname]);

  const iconBox = icon && (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 40, height: 40, borderRadius: 1.5, flexShrink: 0,
        bgcolor: theme.palette.mode === 'light' ? `${accentColor}18` : 'rgba(255,255,255,0.08)',
        transition: 'background-color 0.3s ease',
        '& svg': { fontSize: 22, color: accentColor },
      }}
    >
      {icon}
    </Box>
  );

  const titleBlock = (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="subtitle1"
        fontWeight={700}
        sx={{
          color: theme.palette.mode === 'light' ? '#0A395F' : theme.palette.text.primary,
          lineHeight: 1.2,
          fontFamily: '"Public Sans", sans-serif',
          whiteSpace: isMobile ? 'normal' : 'nowrap',
        }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontFamily: '"Public Sans", sans-serif' }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  );

  return (
    <Card
      elevation={3}
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        borderTop: `4px solid ${accentColor}`,
        transition: 'border-color 0.3s ease',
        mb: 2,
        ...sx,
      }}
    >
      <CardContent
        sx={{
          pb: '12px !important',
          pt: 2,
          px: { xs: 2, sm: 3 },
          bgcolor: theme.palette.mode === 'light' ? '#f8fafc' : 'background.paper',
        }}
      >
        {/* ── Breadcrumbs ── */}
        <Breadcrumbs
          separator={<NavigateNextIcon sx={{ fontSize: 14 }} />}
          aria-label="breadcrumb"
          sx={{ mb: 1.25 }}
        >
          <Link
            href="/dashboard"
            underline="hover"
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.4,
              color: 'text.secondary', fontSize: '0.75rem',
              fontFamily: '"Public Sans", sans-serif',
              '&:hover': { color: 'primary.main' },
            }}
          >
            <HomeRoundedIcon sx={{ fontSize: 14 }} />
            {!isMobile && 'Inicio'}
          </Link>

          {/* Módulo y sección solo en desktop para no saturar mobile */}
          {!isMobile && breadcrumb?.module && (
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', fontFamily: '"Public Sans", sans-serif' }}>
              {breadcrumb.module}
            </Typography>
          )}
          {!isMobile && breadcrumb?.section && (
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', fontFamily: '"Public Sans", sans-serif' }}>
              {breadcrumb.section}
            </Typography>
          )}

          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.primary', fontFamily: '"Public Sans", sans-serif' }}>
            {title}
          </Typography>
        </Breadcrumbs>

        <Divider sx={{ mb: 1.5 }} />

        {isMobile ? (
          /* ── Layout MOBILE: columna ── */
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {/* Fila 1: ícono + título */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {iconBox}
              {titleBlock}
            </Box>

            {/* Fila 2: controles a ancho completo */}
            {children && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%' }}>
                {children}
              </Box>
            )}
          </Box>
        ) : (
          /* ── Layout DESKTOP: título izq | children centrados | rightContent der ── */
          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', minHeight: 48 }}>

            {/* Izquierda */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0, zIndex: 1 }}>
              {iconBox}
              {titleBlock}
            </Box>

            {/* Centro: absolutamente centrado */}
            {children && (
              <Box
                sx={{
                  position: 'absolute', left: 0, right: 0,
                  display: 'flex', justifyContent: 'center', alignItems: 'center',
                  pointerEvents: 'none',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pointerEvents: 'auto' }}>
                  {children}
                </Box>
              </Box>
            )}

            {/* Derecha */}
            {rightContent && (
              <Box sx={{ ml: 'auto', flexShrink: 0, zIndex: 1 }}>
                {rightContent}
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
